import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Video as VideoIcon,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Public tokenized Zoom report form.
 *
 * Route: /zoom-report/:token
 *
 * The Associate Manager receives a tokenized link by email after a Calendly
 * booking. The same Mongo `zoom_reports` document is also reachable from the
 * internal CRM (Lead detail), so compiling EITHER place is equivalent.
 *
 * - GET  /api/public/zoom-report/:token  → prefill data
 * - POST /api/public/zoom-report/:token  → submission
 *
 * On success → calls trigger admin emails (Morena always; Eufemia if positive).
 */
export default function ZoomReportPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [lead, setLead] = useState(null);

  const [attended, setAttended] = useState('yes');
  const [transcript, setTranscript] = useState('');
  const [proposals, setProposals] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // Fetch prefill on mount
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/public/zoom-report/${token}`)
      .then((r) => {
        if (!alive) return;
        setReport(r.data?.report || null);
        setLead(r.data?.lead || null);
        const rep = r.data?.report || {};
        if (rep.attended != null) setAttended(rep.attended ? 'yes' : 'no');
        if (rep.transcript) setTranscript(rep.transcript);
        if (rep.proposals) setProposals(rep.proposals);
      })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 404) {
          setError('Il link è scaduto o non valido. Compila il report direttamente nel CRM.');
        } else {
          setError('Errore nel caricamento. Riprova fra qualche minuto.');
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  const isAlreadySubmitted = report?.status === 'submitted';

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting || isAlreadySubmitted) return;
    if (!proposals.trim() && attended === 'yes') {
      const confirmEmpty = window.confirm(
        'Hai indicato che il cliente era presente ma non hai inserito proposte. Confermi? (puoi aggiungere proposte adesso)'
      );
      if (!confirmEmpty) return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/public/zoom-report/${token}`, {
        attended: attended === 'yes',
        transcript,
        proposals,
      });
      setDone(res.data || { ok: true });
      toast.success('Report inviato. Grazie!');
    } catch (e2) {
      const status = e2?.response?.status;
      if (status === 404) {
        toast.error('Link non valido o scaduto.');
      } else {
        toast.error('Errore durante l\u2019invio. Riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- UI states ----------
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-1)] p-6">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Caricamento report…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-1)] p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 space-y-4 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <h1 className="font-serif text-xl text-[hsl(var(--primary))]">Link non disponibile</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Link to="/" className="inline-block">
              <Button variant="outline" data-testid="zoom-report-back-home">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Torna alla home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--surface-1)] p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 space-y-4 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h1 className="font-serif text-2xl text-[hsl(var(--primary))]">
              {done.already_submitted ? 'Report già inviato' : 'Report inviato'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {done.already_submitted
                ? 'Questo report è già stato inviato in precedenza. Nessuna nuova notifica è stata generata.'
                : 'Grazie. Il report è stato registrato nel CRM e l\u2019admin ha ricevuto la notifica via email.'}
            </p>
            {done.positive && (
              <div className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Esito positivo
              </div>
            )}
            <Link to="/" className="inline-block">
              <Button variant="outline" data-testid="zoom-report-done-home">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Torna alla home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Form ----------
  return (
    <div className="min-h-screen bg-[var(--surface-1)] py-10 px-4 md:px-6">
      <div className="max-w-2xl mx-auto" data-testid="zoom-report-page">
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--gp-gold)] font-semibold">
            Green Paradise · CRM
          </div>
          <h1 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] mt-1">
            Report post-Zoom
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Compila l'esito della call. Il modulo è collegato al CRM: se preferisci,
            puoi compilarlo anche dall'app interna sul lead — è lo stesso record.
          </p>
        </div>

        <Card className="border-border">
          <CardContent className="p-5 md:p-6 space-y-5">
            {/* Lead context */}
            <div className="rounded-lg bg-[var(--surface-2)] border border-border p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lead</div>
                <div className="font-medium" data-testid="zoom-report-lead-name">
                  {lead?.name || '—'}
                </div>
                <div className="text-xs text-muted-foreground">{lead?.email || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Senior</div>
                <div className="font-medium">{report?.senior_name || '—'}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Meeting
                </div>
                <div className="text-xs font-medium">{report?.meeting_at || '—'}</div>
              </div>
              {report?.meeting_url && (
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                    <VideoIcon className="w-3 h-3" /> Link
                  </div>
                  <a
                    href={report.meeting_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[hsl(var(--primary))] underline truncate inline-block max-w-full"
                  >
                    {report.meeting_url}
                  </a>
                </div>
              )}
            </div>

            {isAlreadySubmitted && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Questo report risulta già inviato (
                  <code className="bg-emerald-100 px-1 rounded">submitted</code>). Puoi
                  consultare i dati ma non è più necessario re-inviarlo.
                </span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-5">
              {/* Attended */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Il cliente era presente alla call?</Label>
                <RadioGroup
                  value={attended}
                  onValueChange={setAttended}
                  className="flex gap-4"
                  disabled={isAlreadySubmitted}
                  data-testid="zoom-report-attended-group"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="yes" id="att-yes" data-testid="zoom-report-attended-yes" />
                    <span className="text-sm">Sì, era presente</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="no" id="att-no" data-testid="zoom-report-attended-no" />
                    <span className="text-sm">No-show</span>
                  </label>
                </RadioGroup>
              </div>

              {/* Transcript */}
              <div className="space-y-2">
                <Label htmlFor="transcript" className="text-sm font-medium">
                  Trascrizione / sintesi della call
                </Label>
                <Textarea
                  id="transcript"
                  rows={5}
                  placeholder="Punti chiave emersi, obiezioni, segnali di interesse, prossimi passi…"
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  disabled={isAlreadySubmitted}
                  data-testid="zoom-report-transcript"
                />
              </div>

              {/* Proposals */}
              <div className="space-y-2">
                <Label htmlFor="proposals" className="text-sm font-medium">
                  Proposte fatte / next step concordati
                </Label>
                <Textarea
                  id="proposals"
                  rows={4}
                  placeholder="Es. 2 quote Spille, visita on-site giugno, invio brochure aggiornata, conferma in 48h…"
                  value={proposals}
                  onChange={(e) => setProposals(e.target.value)}
                  disabled={isAlreadySubmitted}
                  data-testid="zoom-report-proposals"
                />
                <p className="text-[11px] text-muted-foreground">
                  La presenza di proposte concrete attiva la notifica con
                  esito positivo al management.
                </p>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] text-muted-foreground">
                  Il modulo è collegato al CRM. Stesso record, due punti d'accesso.
                </div>
                <Button
                  type="submit"
                  disabled={submitting || isAlreadySubmitted}
                  data-testid="zoom-report-submit"
                  className="min-w-[140px]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Invio…
                    </>
                  ) : isAlreadySubmitted ? (
                    'Già inviato'
                  ) : (
                    'Invia report'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="mt-4 text-[11px] text-muted-foreground/80 text-center">
          Green Paradise · HCF Group · Form CONSOB-compliant — i dati sono confidenziali.
        </p>
      </div>
    </div>
  );
}
